import styled, { css } from 'styled-components'
import { Avatar } from '@components/Avatar'
import { Pill } from '@components/Pill'
import { black, border1, grey, mutedGrey, orange, textMuted, warmTint, white } from '@/theme/colors'
import { flatButton } from '@/theme/recipes'
import { motion, radii } from '@/theme/tokens'
import { text } from '@/theme/typography'

/** A card's leading tile: its thumbnail, else its glyph on the warm square. */
export const IconTile = styled.span<{ $size: number; $dimmed?: boolean }>`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: ${({ $size }) => $size}px;
	height: ${({ $size }) => $size}px;
	border-radius: ${radii.small};
	overflow: hidden;
	background: ${warmTint};
	color: ${orange};
	opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
`

export const IconThumbnail = styled.img`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
`

const cardSurface = css<{ $removed: boolean }>`
	display: flex;
	gap: 10px;
	padding: 10px;
	min-width: 0;
	text-align: left;
	border-radius: ${radii.inner};
	border: 1px solid ${border1};
	background: ${({ $removed }) => ($removed ? grey : white)};
	color: ${black};
`

/** A tagged resource in the stream, in full: every leg of a booking and the people on it. */
export const StreamCard = styled.div<{ $removed: boolean }>`
	${cardSurface}
	align-items: flex-start;
	width: 100%;
`

/**
 * The same card when it opens its resource: a tile, so a raw button (see the raw-button
 * ratchet), tinting on hover the way an inner card does.
 */
export const StreamCardButton = styled.button<{ $removed: boolean }>`
	${flatButton}
	${cardSurface}
	align-items: flex-start;
	width: 100%;
	transition: background-color ${motion.fast};

	&:hover {
		background: ${warmTint};
	}
`

/** Attached in the composer: a compact chip with a remove button. */
export const AttachedCard = styled.div<{ $removed: boolean }>`
	${cardSurface}
	align-items: center;
	flex-shrink: 0;
	max-width: 240px;
`

export const CardText = styled.span<{ $removed?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 3px;
	min-width: 0;
	flex: 1;
	opacity: ${({ $removed }) => ($removed ? 0.7 : 1)};
`

export const CardTitle = styled.span<{ $compact?: boolean; $removed?: boolean }>`
	${({ $compact }) => ($compact ? text.labelSm : text.label)}
	color: ${({ $removed }) => ($removed ? mutedGrey : black)};
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: ${({ $compact }) => ($compact ? 1 : 2)};
	-webkit-box-orient: vertical;
	word-break: break-word;
`

export const CardDetail = styled.span<{ $lines?: number }>`
	${text.caption}
	font-weight: 400;
	color: ${textMuted};
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: ${({ $lines = 1 }) => $lines};
	-webkit-box-orient: vertical;
`

export const LegRoute = styled.span`
	${text.labelSm}
	color: ${black};
`

export const Leg = styled.span`
	display: flex;
	flex-direction: column;
	gap: 1px;
`

export const CategoryPill = styled(Pill).attrs({ $tone: 'accent', $size: 'sm' })<{ $removed?: boolean }>`
	${({ $removed }) => $removed && `background: ${mutedGrey};`}
`

export const Facepile = styled.span`
	display: inline-flex;
	padding-top: 4px;
	padding-left: 4px;
`

export const FacepileAvatar = styled(Avatar)`
	margin-left: -4px;
`
